import React, { WheelEvent, useCallback, useMemo } from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewProps,
} from 'react-native';
import Animated, {
  AnimatableValue,
  AnimationCallback,
  cancelAnimation,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withDecay,
  withTiming,
} from 'react-native-reanimated';
import {
  Gesture,
  GestureDetector,
  GestureStateChangeEvent,
  GestureUpdateEvent,
  PinchGestureHandlerEventPayload,
  TapGestureHandlerEventPayload,
} from 'react-native-gesture-handler';

export type ViewZoomRef = {
  onScaleChange: (args: { scale: number }) => void;
  onPan: (arg: { x: number; y: number }) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomReset: () => void;
  setEnabled: (enabled: boolean) => void;
  onSingleTap: (event: { x: number; y: number }) => void;
};

interface ViewZoomProps {
  onScaleChange: (args: { scale: number }) => void;
  onPan: ViewZoomRef['onPan'];
  onSingleTap: ViewZoomRef['onSingleTap'];
  style?: StyleProp<ViewProps>;
  contentContainerStyle?: StyleProp<ViewProps>;
  animationConfig?: object;
  animationFunction?<T extends AnimatableValue>(
    toValue: T,
    userConfig?: object,
    callback?: AnimationCallback,
  ): T;
  maxZoom?: number;
  minZoom?: number;
  enabled?: boolean;
  animate?: boolean;
  contentsSize: { width: number; height: number };
  ref: React.Ref<ViewZoomRef>;
}

export const ViewZoom = ({
  style,
  contentContainerStyle,
  children,
  animationFunction = withTiming,
  animationConfig,
  onScaleChange,
  onPan,
  onSingleTap,
  minZoom = 1,
  maxZoom = 6,
  animate = true,
  enabled = true,
  contentsSize,
  ref,
}: React.PropsWithChildren<ViewZoomProps>) => {
  const containerRef = React.useRef<View>(null);
  const baseScale = useSharedValue(1);
  const pinchScale = useSharedValue(1);
  const lastScale = useSharedValue(1);
  const lastChangeTime = useSharedValue(0);
  const containerDimensions = useSharedValue({ width: 0, height: 0 });
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);

  const lastKnownIncomingEnabledRef = React.useRef(enabled);
  const [internalEnabled, setInternalEnabled] = React.useState(enabled);

  React.useEffect(() => {
    if (enabled !== lastKnownIncomingEnabledRef.current) {
      lastKnownIncomingEnabledRef.current = enabled;
      setInternalEnabled(enabled);
    }
  }, [enabled]);

  const withAnimation = useCallback(
    (toValue: number, config?: object) => {
      'worklet';

      return animationFunction(toValue, {
        duration: 350,
        ...config,
        ...animationConfig,
      });
    },
    [animationFunction, animationConfig],
  );

  const getScaleRounded = useCallback((scale: number): number => {
    'worklet';

    return Math.round((scale + Number.EPSILON) * 100) / 100;
  }, []);

  const getContainerSize = useCallback(() => {
    return {
      width: containerDimensions.value.width,
      height: containerDimensions.value.height,
    };
  }, [containerDimensions.value.height, containerDimensions.value.width]);

  const zoomIn = useCallback(
    ({
      scale,
      zoomTo,
    }: {
      scale?: number;
      zoomTo?: { x: number; y: number };
    }): void => {
      const { width, height } = getContainerSize();
      const lastScaleValue = lastScale.value;
      const newScale = Math.min(
        scale !== undefined ? scale : lastScaleValue + 1,
        maxZoom,
      );

      if (zoomTo) {
        translateX.value = withAnimation(
          calculatedZoomedCenter(
            translateX.value,
            width,
            lastScaleValue,
            newScale,
            zoomTo.x,
          ),
        );
        translateY.value = withAnimation(
          calculatedZoomedCenter(
            translateY.value,
            height,
            lastScaleValue,
            newScale,
            zoomTo.y,
          ),
        );
      }

      if (newScale !== lastScaleValue) {
        lastScale.value = newScale;
        baseScale.value = animate ? withAnimation(newScale) : newScale;
        pinchScale.value = animate ? withAnimation(1) : 1;
        onScaleChange({ scale: newScale });
      }
    },
    [
      getContainerSize,
      lastScale,
      maxZoom,
      translateX,
      withAnimation,
      translateY,
      baseScale,
      animate,
      pinchScale,
      onScaleChange,
    ],
  );

  const zoomOut = useCallback(
    ({ scale }: { scale?: number }): void => {
      const newScale = Math.max(
        scale !== undefined ? scale : lastScale.value - 1,
        minZoom,
      );
      if (newScale === lastScale.value) {
        return;
      }
      lastScale.value = newScale;
      baseScale.value = animate ? withAnimation(newScale) : newScale;
      pinchScale.value = animate ? withAnimation(1) : 1;
      onScaleChange({ scale: newScale });
    },
    [
      lastScale,
      minZoom,
      baseScale,
      animate,
      withAnimation,
      pinchScale,
      onScaleChange,
    ],
  );

  const zoomReset = React.useCallback((): void => {
    cancelAnimation(translateX);
    cancelAnimation(translateY);
    cancelAnimation(baseScale);
    cancelAnimation(pinchScale);
    lastScale.value = minZoom;
    translateX.value = animate ? withAnimation(0) : 0;
    translateY.value = animate ? withAnimation(0) : 0;
    baseScale.value = animate ? withAnimation(minZoom) : minZoom;
    pinchScale.value = animate ? withAnimation(1) : 1;
    onScaleChange({ scale: minZoom });
  }, [
    lastScale,
    minZoom,
    translateX,
    animate,
    withAnimation,
    translateY,
    baseScale,
    pinchScale,
    onScaleChange,
  ]);

  const onDoubleTap = useCallback(
    (event: GestureStateChangeEvent<TapGestureHandlerEventPayload>): void => {
      if (lastScale.value >= 4) {
        zoomReset();
      } else {
        zoomIn({ zoomTo: { x: event.x, y: event.y } });
      }
    },
    [zoomIn, lastScale.value, zoomReset],
  );

  const handleSingleTap = useCallback(
    (event: GestureStateChangeEvent<TapGestureHandlerEventPayload>): void => {
      const scaleFactor = lastScale.value;
      const containerWidth = containerDimensions.value.width;
      const containerHeight = containerDimensions.value.height;
      const pannedY = translateY.value * scaleFactor;
      const pannedX = translateX.value * scaleFactor;
      const contentsWidthHalf = (contentsSize.width * scaleFactor) / 2;
      const contentsHeightHalf = (contentsSize.height * scaleFactor) / 2;
      // Calculate canvas center
      const x0 = containerWidth / 2 + pannedX;
      const y0 = containerHeight / 2 + pannedY;
      // Calculate content left x and top y
      const contentsX = x0 - contentsWidthHalf;
      const contentsY = y0 - contentsHeightHalf;
      // Calculate event x and y
      const finalX = (event.x - contentsX) / scaleFactor;
      const finalY = (event.y - contentsY) / scaleFactor;
      if (
        finalX < 0 ||
        finalY < 0 ||
        finalX > contentsSize.width ||
        finalY > contentsSize.height
      ) {
        return;
      }
      onSingleTap({ x: finalX, y: finalY });
    },
    [
      contentsSize,
      lastScale.value,
      containerDimensions.value.width,
      containerDimensions.value.height,
      translateX.value,
      translateY.value,
      onSingleTap,
    ],
  );

  const onLayout = useCallback(
    ({
      nativeEvent: {
        layout: { width, height },
      },
    }: LayoutChangeEvent): void => {
      containerDimensions.value = {
        width,
        height,
      };
    },
    [containerDimensions],
  );

  const onPinching = useCallback(
    (scale: number) => {
      const newScale = getScaleRounded(lastScale.value * scale);
      onScaleChange({ scale: newScale });
    },
    [getScaleRounded, lastScale.value, onScaleChange],
  );
  const onPinchEnd = useCallback(
    (scale: number): void => {
      const newScale = getScaleRounded(lastScale.value * scale);
      if (newScale < minZoom) {
        zoomReset();
      } else {
        pinchScale.value = 1;
        lastScale.value = newScale;
        baseScale.value = newScale;
        onScaleChange({ scale: newScale });
      }
    },
    [
      getScaleRounded,
      lastScale,
      minZoom,
      zoomReset,
      pinchScale,
      baseScale,
      onScaleChange,
    ],
  );

  const handleWheelChange = React.useCallback(
    (event: WheelEvent<View>) => {
      const changeValue = event.deltaY < 0 ? -1 : 1;
      const newScale = Math.floor(lastScale.value + changeValue);
      if (newScale > lastScale.value) {
        const targetRect = containerRef.current?.getBoundingClientRect();
        if (!targetRect) return;
        const x = event.clientX - targetRect.left;
        const y = event.clientY - targetRect.top;
        zoomIn({
          scale: newScale,
          zoomTo: { x, y },
        });
      } else if (newScale < lastScale.value) {
        zoomOut({ scale: newScale });
      }
    },
    [lastScale.value, containerRef, zoomIn, zoomOut],
  );

  useAnimatedReaction(
    () => ({
      x: translateX.value,
      y: translateY.value,
    }),
    (current) =>
      runOnJS(onPan)({
        x: current.x,
        y: current.y,
      }),
  );

  const zoomGestures = useMemo(() => {
    const doubleTapGesture = Gesture.Tap()
      .numberOfTaps(2)
      .onEnd((event) => runOnJS(onDoubleTap)(event))
      .enabled(internalEnabled);

    const tapGesture = Gesture.Tap()
      .numberOfTaps(1)
      .onEnd((event) => runOnJS(handleSingleTap)(event))
      .enabled(internalEnabled);

    const panGesture = Gesture.Pan()
      .onChange((event) => {
        translateX.value += event.changeX / lastScale.value;
        translateY.value += event.changeY / lastScale.value;
        lastChangeTime.value = Date.now();
      })
      .onFinalize((event) => {
        const recentChange = lastChangeTime.value + 20 > Date.now();
        if (recentChange) {
          if (event.velocityX > 200 || event.velocityX < -200) {
            translateX.value = withDecay({
              velocity: event.velocityX,
              rubberBandEffect: false,
              velocityFactor: Platform.OS === 'web' ? 0.2 : 0.3,
              // clamp: [-50, 900],
            });
          }
          if (event.velocityY > 200 || event.velocityY < -200) {
            translateY.value = withDecay({
              velocity: event.velocityY,
              rubberBandEffect: false,
              velocityFactor: Platform.OS === 'web' ? 0.2 : 0.3,
              // clamp: [-50, 900],
            });
          }
        }
      })
      .minDistance(5)
      .minPointers(1)
      .maxPointers(2)
      .enabled(internalEnabled);

    const pinchGesture = Gesture.Pinch()
      .onUpdate(
        ({
          scale,
        }: GestureUpdateEvent<PinchGestureHandlerEventPayload>): void => {
          const newScale = Math.min(baseScale.value * scale, maxZoom);
          const finalScale = getScaleRounded(newScale / baseScale.value);
          pinchScale.value = finalScale;
          runOnJS(onPinching)(finalScale);
        },
      )
      .onEnd(
        ({
          scale,
        }: GestureUpdateEvent<PinchGestureHandlerEventPayload>): void => {
          const newScale = Math.min(baseScale.value * scale, maxZoom);
          const finalScale = getScaleRounded(newScale / baseScale.value);
          pinchScale.value = finalScale;
          runOnJS(onPinchEnd)(finalScale);
        },
      )
      .enabled(internalEnabled);

    return Gesture.Race(
      Gesture.Simultaneous(pinchGesture, panGesture),
      Gesture.Exclusive(doubleTapGesture, tapGesture),
    );
  }, [
    internalEnabled,
    onDoubleTap,
    handleSingleTap,
    translateX,
    lastScale.value,
    translateY,
    lastChangeTime,
    baseScale.value,
    maxZoom,
    getScaleRounded,
    pinchScale,
    onPinching,
    onPinchEnd,
  ]);

  const animContentContainerStyle = useAnimatedStyle(() => ({
    transform: [
      {
        scale: getScaleRounded(baseScale.value * pinchScale.value),
      },
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
  }));

  React.useImperativeHandle(
    ref,
    () => ({
      zoomIn: () => zoomIn({}),
      zoomOut: () => zoomOut({}),
      setEnabled: setInternalEnabled,
      zoomReset,
      onScaleChange,
      onPan,
      onSingleTap,
    }),
    [onPan, onScaleChange, onSingleTap, zoomIn, zoomOut, zoomReset],
  );

  return (
    <GestureDetector gesture={zoomGestures}>
      <View
        ref={containerRef}
        style={[styles.container, style]}
        onLayout={onLayout}
        collapsable={false}
        // @ts-expect-error web has onWheel
        onWheel={internalEnabled ? handleWheelChange : undefined}
      >
        <Animated.View
          style={[animContentContainerStyle, contentContainerStyle]}
        >
          {children}
        </Animated.View>
      </View>
    </GestureDetector>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
});

function calculatedZoomedCenter(
  oldOffsetXOrYScaled: number,
  zoomSubjectOriginalWidthOrHeight: number,
  oldScale: number,
  newScale: number,
  zoomCenterXOrY: number,
) {
  const oldOffSetUnscaled = oldOffsetXOrYScaled * oldScale;
  const scaleRatio = newScale / oldScale;

  const zsOriginalCenter = {
    xOrY: 0,
    distanceToZoomCenter: 0,
  };
  const zsCurrentCenter = {
    xOrY: 0,
    distanceToZoomCenter: 0,
  };
  const zsNewCenter = {
    xOrY: 0,
    distanceToZoomCenter: 0,
  };

  zsOriginalCenter.xOrY = zoomSubjectOriginalWidthOrHeight / 2;
  zsCurrentCenter.xOrY = zsOriginalCenter.xOrY + oldOffSetUnscaled;
  zsCurrentCenter.distanceToZoomCenter = zsCurrentCenter.xOrY - zoomCenterXOrY;

  zsNewCenter.distanceToZoomCenter =
    zsCurrentCenter.distanceToZoomCenter * scaleRatio;
  zsNewCenter.xOrY = zsNewCenter.distanceToZoomCenter + zoomCenterXOrY;

  const newOffsetUnscaled = zsNewCenter.xOrY - zsOriginalCenter.xOrY;

  return newOffsetUnscaled / newScale;
}
